import { useEffect, useMemo, useState } from 'react';
import { layoutApi, type WidgetDef } from '../api';
import { WidgetView } from '../components/Widgets';
import { PinButton } from '../components/PinButton';

/**
 * Bladerpagina voor een netwerk (HBO, Netflix…), studio (Pixar, A24…) of categorie
 * ("Gebaseerd op een boek"): wat je al hebt, het populairste en het nieuwste, met Aanvragen.
 */
export function Browse({ kind, id, media }: { kind: 'company' | 'network' | 'keyword'; id: number; media: 'movie' | 'tv' | null }) {
  const [info, setInfo] = useState<{ name: string; logo: string | null } | null>(null);

  useEffect(() => {
    layoutApi.brand(kind, id).then(setInfo).catch(() => setInfo({ name: kind === 'keyword' ? 'Categorie' : 'Studio', logo: null }));
  }, [kind, id]);

  const name = info?.name ?? '';
  const rows = useMemo<WidgetDef[]>(() => {
    const list = kind;
    const w = (key: string, title: string, m: 'movie' | 'tv', extra: Partial<WidgetDef> = {}, sort?: 'new'): WidgetDef => ({
      id: `${key}-${m}`, title, view: 'poster', show: 'all', limit: 30, enabled: true,
      source: { kind: 'tmdb', list, media: m, refId: id, refName: name, sort }, ...extra,
    });
    const types: ('movie' | 'tv')[] = kind === 'network' ? ['tv'] : media ? [media, media === 'movie' ? 'tv' : 'movie'] : ['movie', 'tv'];
    const label = (m: 'movie' | 'tv') => (m === 'movie' ? 'films' : 'series');
    return types.flatMap((m, i) => [
      ...(i === 0 ? [w('owned', `In je bibliotheek`, m, { show: 'owned', view: 'landscape' })] : []),
      w('popular', `Populairste ${label(m)}`, m, i === 0 ? { view: 'poster' } : {}),
      w('new', `Nieuwste ${label(m)}`, m, {}, 'new'),
    ]);
  }, [kind, id, media, name]);

  return (
    <main className="page browse-page">
      <header className="browse-head">
        {info?.logo ? <span className="browse-logo"><img src={info.logo} alt={info.name} /></span> : <h1>{name}</h1>}
        {info?.logo && <h1 className="visually-hidden">{name}</h1>}
        <span className="subtle">{kind === 'network' ? 'Netwerk / streamingdienst' : kind === 'company' ? 'Studio' : 'Categorie'}</span>
        {info && (
          <PinButton
            title={kind === 'keyword' ? name : `Van ${name}`}
            source={{ kind: 'tmdb', list: kind, media: kind === 'network' ? 'tv' : media ?? 'movie', refId: id, refName: name }}
          />
        )}
      </header>
      {info && rows.map((w) => <WidgetView key={w.id} w={w} />)}
      <div className="footer-space" />
    </main>
  );
}
